// web48_timer_banner (Ethan, Oct 30 2026): old timer banner vs Oben's new
// banner, both counting down to ONE fixed end date that is the same for every
// visitor (Statsig dynamic config `web48_timer_banner_end`, field `ends_at`).
//
// For a visitor holding a web48 arm the baseline 10-minute offer (web29Offer)
// does not apply: the evergreen 20% offer stays live for the whole test, so
// the price never flips to the regular price, on the pricing page or at
// checkout. Past the end date the banner says "ends today" where the
// countdown was and the price is unchanged (Steven, Oct 30).
//
// App writes this state whenever Statsig resolves the visitor (arm + current
// end date) and clears it when the visitor has no arm, so web29Offer and the
// checkout (a full page load that reads the offer before Statsig is ready)
// can read it synchronously.
export const WEB48_STORAGE_KEY = "bws_web48";

export type Web48Arm = "control" | "variation";

export interface Web48OfferState {
  arm: Web48Arm;
  // ms epoch; undefined when the config has no valid end date (no countdown).
  endsAt?: number;
}

export function readWeb48Offer(): Web48OfferState | null {
  try {
    const raw = localStorage.getItem(WEB48_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.arm !== "control" && parsed.arm !== "variation")) return null;
    return {
      arm: parsed.arm,
      endsAt: typeof parsed.endsAt === "number" ? parsed.endsAt : undefined,
    };
  } catch {
    return null;
  }
}

export function writeWeb48Offer(state: Web48OfferState | null): void {
  try {
    if (state) localStorage.setItem(WEB48_STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(WEB48_STORAGE_KEY);
  } catch {
    // Storage unavailable: the visitor falls back to the baseline offer.
  }
}

// `ends_at` from the dynamic config: an ISO 8601 date-time with an explicit
// offset (e.g. "2026-11-06T18:00:00Z"). Anything else means no countdown.
export function parseWeb48EndsAt(value: unknown): number | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : undefined;
}

export interface Web48Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function toWeb48Countdown(remainingMs: number): Web48Countdown {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}
